// src/shared/components/DeviceRouter.js
// Conditional rendering based on device type with code splitting

import React, { Suspense, lazy } from 'react';
import { useDeviceContext, DEVICE_TYPES } from '../context/DeviceContext';

// Lazy load device-specific apps for code splitting
// This ensures mobile users don't download desktop code and vice versa
const MobileApp = lazy(() => import('../../mobile/MobileApp'));
const TabletApp = lazy(() => import('../../tablet/TabletApp'));

// Loading skeleton component
const LoadingSkeleton = () => (
  <div
    style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #050816 0%, #0A0D1A 40%, #1A1A2E 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div
      style={{
        width: '48px',
        height: '48px',
        border: '3px solid rgba(139, 92, 246, 0.2)',
        borderTopColor: '#8B5CF6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    />
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

/**
 * DeviceRouter - Renders the appropriate app version based on device type
 *
 * @param {Object} props
 * @param {React.ComponentType} props.desktopComponent - Component to render on desktop
 * @param {React.ComponentType} [props.mobileComponent] - Optional component to render on mobile (defaults to MobileApp)
 * @param {React.ComponentType} [props.tabletComponent] - Optional component to render on tablet (defaults to TabletApp)
 * @param {React.ReactNode} [props.fallback] - Custom loading fallback
 */
const DeviceRouter = ({
  desktopComponent: DesktopComponent,
  mobileComponent: MobileComponent,
  tabletComponent: TabletComponent,
  fallback,
}) => {
  const { deviceType } = useDeviceContext();

  const renderContent = () => {
    switch (deviceType) {
      case DEVICE_TYPES.MOBILE:
        // Use provided mobile component or default to MobileApp
        if (MobileComponent) {
          return <MobileComponent />;
        }
        return <MobileApp />;

      case DEVICE_TYPES.TABLET:
        // Use provided tablet component, fall back to mobile component, or default to TabletApp
        if (TabletComponent) {
          return <TabletComponent />;
        }
        if (MobileComponent) {
          return <MobileComponent />;
        }
        return <TabletApp />;

      case DEVICE_TYPES.DESKTOP:
      default:
        return <DesktopComponent />;
    }
  };

  return (
    <Suspense fallback={fallback || <LoadingSkeleton />}>
      {renderContent()}
    </Suspense>
  );
};

export default DeviceRouter;
